import { Check, ChevronDown, Edit05, FilePlus02, SearchMd, X } from "@untitledui/icons";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Checkbox as AriaCheckbox, Label } from "react-aria-components";
import { useAddToCreatorList, useCreateCreatorList, useCreatorLists, useEditCreatorList } from "@/api/creator-lists";
import { Button } from "@/components/base/buttons/button";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { CheckboxGroup } from "@/components/base/checkbox-groups/checkbox-groups";
import { Input } from "@/components/base/input/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useDebounce } from "@/hooks/use-debounce";
import { error, success } from "@/providers/ToastProvider";
import { pluralize } from "@/utils/text";
import { ListsTable } from "./components/lists-table";
import { CreateListForm } from "./create-list-form";

type LocalList = {
  id: string;
  name: string;
  creatorCount: number;
  description?: string | null;
  createdAt?: string;
  isNew?: boolean;
  isPending?: boolean; // Not yet created in the API
};

export const AddtoListsModal = ({
  selectedCreators,
  onAddToList,
  children,
  open: controlledOpen,
  onOpenChange,
}: {
  onAddToList: () => void;
  selectedCreators: string[];
  /**
   * Optional trigger element. When provided, the modal manages its own open
   * state via Radix's `DialogTrigger`. Omit when using the controlled
   * `open` / `onOpenChange` props instead — this is preferred inside table
   * rows so we don't have to dispatch a synthetic click on a hidden button
   * (which bubbles into row press handlers, see UNB-1368).
   */
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [selected, setSelected] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [localLists, setLocalLists] = useState<LocalList[]>([]);
  const [newlyCreatedIds, setNewlyCreatedIds] = useState<Set<string>>(new Set());
  const [editingListId, setEditingListId] = useState<string | null>(null);
  const [editingListName, setEditingListName] = useState("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(search);
  const { data: lists, isFetching } = useCreatorLists({ offset: 0, limit: 50 }, debouncedSearch, {
    column: "createdAt",
    direction: "asc",
  });
  const { mutateAsync } = useAddToCreatorList();
  const { mutateAsync: createList } = useCreateCreatorList();
  const { mutateAsync: editList } = useEditCreatorList();

  useEffect(() => {
    if (!open) {
      setSelected([]);
      setShowCreateForm(false);
      setNewListName("");
      setLocalLists([]);
      setNewlyCreatedIds(new Set());
      setEditingListId(null);
      setEditingListName("");
      setSortDirection("desc");
      setSearch("");
      setStoredInitialCount(0);
    }
  }, [open]);

  // Merge server lists with locally created lists
  const allLists = useMemo(() => {
    const serverLists = (lists?.lists ?? []).map((list) => ({
      ...list,
      isNew: false,
      createdAt: list.createdAt,
    }));
    const serverListIds = new Set(serverLists.map((list) => list.id));

    // Filter out local lists that are already in server lists (to avoid duplicates)
    const uniqueLocalLists = localLists.filter((list) => !serverListIds.has(list.id));
    let merged: LocalList[] = [...uniqueLocalLists, ...serverLists];

    // Apply search filter
    if (debouncedSearch) {
      const lowered = debouncedSearch.toLowerCase();
      merged = merged.filter((list) => list.name.toLowerCase().includes(lowered));
    }

    // Sort by created date
    merged.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return sortDirection === "desc" ? dateB - dateA : dateA - dateB;
    });

    return merged;
  }, [lists, localLists, debouncedSearch, sortDirection]);

  // Track the initial list count (when no search is active) to determine if search bar should be shown
  const initialListCount = useMemo(() => {
    // Only count when there's no search active (this represents the "full" list)
    if (debouncedSearch === "") {
      const serverCount = (lists?.lists ?? []).length;
      const serverListIds = new Set((lists?.lists ?? []).map((list) => list.id));
      const uniqueLocalCount = localLists.filter((list) => !serverListIds.has(list.id)).length;
      return serverCount + uniqueLocalCount;
    }
    // Return previous value if search is active (don't recalculate)
    return undefined;
  }, [lists, localLists, debouncedSearch]);

  // Store the initial count in a ref so it persists across searches
  const [storedInitialCount, setStoredInitialCount] = useState(0);

  useEffect(() => {
    if (initialListCount !== undefined) {
      setStoredInitialCount(initialListCount);
    }
  }, [initialListCount]);

  // Maintain focus on search input while user is typing (after query refetches)
  // biome-ignore lint/correctness/useExhaustiveDependencies: lists is intentionally included to re-focus after query updates
  useEffect(() => {
    if (search !== "" && searchInputRef.current && document.activeElement !== searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [lists, search]);

  // Check if we only have pending lists (no real server lists)
  const hasOnlyPendingLists = useMemo(() => {
    return allLists.length > 0 && allLists.every((list) => list.isPending);
  }, [allLists]);

  // Determine if we should show empty state
  const shouldShowEmptyState = storedInitialCount === 0 && allLists.length === 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Select all / deselect all functionality
  const allListIds = useMemo(() => allLists.map((list) => list.id), [allLists]);
  const allSelected = useMemo(
    () => allListIds.length > 0 && allListIds.every((id) => selected.includes(id)),
    [allListIds, selected],
  );
  const someSelected = useMemo(() => selected.length > 0 && !allSelected, [selected, allSelected]);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelected([]);
    } else {
      setSelected(allListIds);
    }
  };

  const handleCreateList = () => {
    if (!newListName.trim()) return;

    // Create a temporary ID for the pending list
    const tempId = `pending-${Date.now()}`;
    const newList: LocalList = {
      id: tempId,
      name: newListName.trim(),
      creatorCount: 0,
      description: null,
      createdAt: new Date().toISOString(),
      isNew: true,
      isPending: true,
    };

    // Add to local lists and auto-select it
    setLocalLists((prev) => [newList, ...prev]);
    setNewlyCreatedIds((prev) => new Set([...prev, tempId]));
    setSelected((prev) => [...prev, tempId]);

    // Reset form
    setNewListName("");
    setShowCreateForm(false);
  };

  const handleEditList = async (id: string) => {
    if (!editingListName.trim()) return;

    const list = localLists.find((l) => l.id === id);

    // If it's a pending list, just update local state
    if (list?.isPending) {
      setLocalLists((prev) => prev.map((l) => (l.id === id ? { ...l, name: editingListName.trim() } : l)));
      setEditingListId(null);
      setEditingListName("");
      return;
    }

    // Otherwise, update via API
    try {
      await editList({ id, name: editingListName.trim() });

      setLocalLists((prev) => prev.map((l) => (l.id === id ? { ...l, name: editingListName.trim() } : l)));

      setEditingListId(null);
      setEditingListName("");
      success("List name updated");
    } catch (err) {
      error("Failed to update list name");
      throw err;
    }
  };

  const onSubmit = async () => {
    try {
      // First, create any pending lists and get their real IDs
      const pendingLists = localLists.filter((list) => list.isPending && selected.includes(list.id));
      const createdListsMap = new Map<string, string>(); // tempId -> realId

      for (const pendingList of pendingLists) {
        const result = await createList({
          name: pendingList.name,
          creatorIds: selectedCreators, // Add creators directly when creating
        });
        createdListsMap.set(pendingList.id, result.id);
      }

      // Then, add creators to existing (non-pending) lists
      const existingListIds = selected.filter((id) => !id.startsWith("pending-"));

      if (existingListIds.length > 0) {
        await Promise.all(
          existingListIds.map((id) =>
            mutateAsync({
              id,
              creators: selectedCreators,
            }),
          ),
        );
      }

      success("Added users to creator lists");
      onAddToList();
      setOpen(false);
    } catch (err) {
      error("Failed to add to creator list");
      throw err;
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent className="sm:max-w-[50rem] min-h-[40rem]">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
          className="space-y-6 flex flex-col h-full justify-between"
        >
          <div className="h-full">
            <DialogHeader>
              <DialogTitle>
                {selected.length > 0
                  ? `${selected.length} x ${selected.length === 1 ? "List" : "Lists"} selected`
                  : "All lists"}
              </DialogTitle>
            </DialogHeader>

            <div className="relative flex flex-col gap-4 flex-1 min-h-0 mt-8 h-full">
              {shouldShowEmptyState || hasOnlyPendingLists ? (
                // Empty State (truly no lists or only pending lists)
                <div className="h-full flex flex-col justify-center">
                  <div className="flex flex-col bg-gray-50 rounded-lg p-6 min-h-[24rem]">
                    <div className="flex flex-col items-center justify-center flex-1 gap-4 mb-6">
                      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-gray-100">
                        <FilePlus02 className="w-6 h-6 text-gray-600" />
                      </div>
                      <div className="text-center">
                        <h3 className="text-sm font-semibold text-gray-900">No lists yet</h3>
                        <p className="text-sm text-gray-600 mt-1">Create a list to add creators to it.</p>
                      </div>

                      {/* Show pending lists if any */}
                      {hasOnlyPendingLists && (
                        <div className="w-full px-6">
                          <CheckboxGroup
                            value={selected}
                            onChange={setSelected}
                            items={allLists.map((list) => ({
                              value: list.id,
                              title: list.name,
                              secondaryTitle: `${list.creatorCount} creators`,
                              description: list.description ?? undefined,
                            }))}
                            render={(item) => (
                              <div className="w-full">
                                <div className="flex flex-row items-center w-full justify-between gap-2">
                                  {editingListId === item.value ? (
                                    <div className="flex-1 flex items-center gap-2">
                                      <Input
                                        value={editingListName}
                                        onChange={setEditingListName}
                                        className="flex-1"
                                        autoFocus
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") {
                                            e.preventDefault();
                                            handleEditList(item.value);
                                          } else if (e.key === "Escape") {
                                            setEditingListId(null);
                                            setEditingListName("");
                                          }
                                        }}
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleEditList(item.value);
                                        }}
                                        className="text-green-600 hover:text-green-700"
                                        title="Save"
                                      >
                                        <Check className="w-4 h-4" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setEditingListId(null);
                                          setEditingListName("");
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                        title="Cancel"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <>
                                      <Label className="flex gap-2 items-center flex-1">
                                        <span className="text-secondary text-sm font-medium">{item.title}</span>
                                      </Label>
                                      {newlyCreatedIds.has(item.value) ? (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setEditingListId(item.value);
                                            setEditingListName(item.title);
                                          }}
                                          className="text-gray-400 hover:text-brand-primary"
                                        >
                                          <Edit05 className="w-4 h-4" />
                                        </button>
                                      ) : (
                                        <Label className="flex gap-1">
                                          <span className="text-secondary text-xs italic">{item.secondaryTitle}</span>
                                        </Label>
                                      )}
                                    </>
                                  )}
                                </div>
                                {!editingListId && item.description && (
                                  <span className="text-secondary text-xs wrap-break-word">{item.description}</span>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {/* Show Create List button only if no lists at all */}
                      {allLists.length === 0 && !showCreateForm && (
                        <Button
                          type="button"
                          color="secondary"
                          size="md"
                          iconLeading={<FilePlus02 className="w-4 h-4" />}
                          onClick={() => setShowCreateForm(true)}
                        >
                          Create List
                        </Button>
                      )}
                    </div>

                    {/* Show create form at bottom */}
                    {showCreateForm && allLists.length === 0 && (
                      <CreateListForm
                        listName={newListName}
                        onListNameChange={setNewListName}
                        onSubmit={handleCreateList}
                        onCancel={() => {
                          setShowCreateForm(false);
                          setNewListName("");
                        }}
                      />
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Search - show if initial list count (before any search) is more than 8 */}
                  {storedInitialCount > 8 && (
                    <Input
                      ref={searchInputRef}
                      icon={SearchMd}
                      placeholder="Search"
                      value={search}
                      onChange={setSearch}
                      onClear={() => setSearch("")}
                    />
                  )}

                  {/* Table Header */}
                  <div className="flex items-center justify-between px-4 py-5 border-b border-secondary bg-gray-50">
                    <div className="flex items-center gap-3">
                      <AriaCheckbox
                        isSelected={allSelected}
                        isIndeterminate={someSelected}
                        onChange={handleSelectAll}
                        className="flex items-center"
                      >
                        <CheckboxBase isSelected={allSelected} isIndeterminate={someSelected} />
                      </AriaCheckbox>
                      <span className="text-sm font-medium text-tertiary">Name</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSortDirection((prev) => (prev === "desc" ? "asc" : "desc"))}
                      className="flex items-center gap-1 text-sm font-medium text-tertiary hover:text-secondary transition-colors"
                    >
                      Created date
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${sortDirection === "asc" ? "rotate-180" : ""}`}
                      />
                    </button>
                  </div>

                  {/* Lists */}
                  <div className="relative flex-1 min-h-0 h-full">
                    <div className="overflow-y-auto h-full">
                      {isFetching && debouncedSearch ? (
                        <div className="flex items-center justify-center py-12 text-tertiary text-sm gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand border-t-transparent" />
                          <span>Searching...</span>
                        </div>
                      ) : allLists.length > 0 ? (
                        <ListsTable
                          allLists={allLists}
                          selected={selected}
                          onSelectionChange={setSelected}
                          formatDate={formatDate}
                        />
                      ) : (
                        <div className="flex items-center justify-center py-12 text-tertiary text-sm">
                          No lists found
                        </div>
                      )}
                    </div>
                    {/* Gradient overlay at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                  </div>
                </>
              )}
            </div>
          </div>
          {/* Only show footer and create section when not in empty state */}
          {!shouldShowEmptyState && (
            <div className="mt-8">
              {/* Create new list section - moved outside to stay at bottom */}
              {!hasOnlyPendingLists && (
                <>
                  {!showCreateForm && (
                    <div className="flex items-center justify-between pt-4 px-4 py-3 mb-6 border-secondary bg-gray-50 rounded-lg">
                      <span className="text-sm text-tertiary">Can't find the list you want?</span>
                      <Button
                        type="button"
                        color="secondary"
                        size="md"
                        iconLeading={<FilePlus02 className="w-4 h-4" />}
                        onClick={() => setShowCreateForm(true)}
                      >
                        Create new List
                      </Button>
                    </div>
                  )}

                  {/* Create form */}
                  {showCreateForm && (
                    <CreateListForm
                      listName={newListName}
                      onListNameChange={setNewListName}
                      onSubmit={handleCreateList}
                      onCancel={() => {
                        setShowCreateForm(false);
                        setNewListName("");
                      }}
                    />
                  )}
                </>
              )}

              <DialogFooter>
                <Button color="secondary" size="lg" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  isDisabled={!selected.length}
                  color="primary"
                  size="lg"
                  type="submit"
                  iconLeading={<FilePlus02 className="w-5 h-5" />}
                >
                  Add to {pluralize(selected.length, "list", "lists")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};
